import { generateDateQueryParams } from "../utils/generalUtils";
import { getRequest, postRequest } from "./apiService";

/**
 * Uploads a file to a signed URL using a PUT request.
 *
 * @param {File|Blob} file - The file to be uploaded.
 * @param {string} signedUrl - The pre-signed URL provided for uploading the file.
 * @param {string} contentType - The MIME type of the file being uploaded.
 * @returns {Promise<void>} - Logs success or failure of the upload.
 *
 * This function sends the file as a binary stream using the `fetch` API.
 * The `Content-Type` header is set to `application/octet-stream` to ensure proper handling by the server.
 */
async function uploadWithSignedUrl(file, signedUrl, contentType) {
  try {
    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
    });
    if (response.ok) {
      console.log("Upload successful");
      return response.url;
    } else {
      console.error("Upload failed");
    }
  } catch (error) {
    console.error("Upload error:", error);
  }
}

/**
 * Function to generate a signed URL for uploading a file.
 * @param {object} formData - The form data.
 */

export const generateSignedUrl = async (formData) => {
  const url = `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/v1/no_support/signed-url`;
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const result = await postRequest(
    url,
    {
      customer_name: formData.customer_name,
      email: formData.email,
      fileName: formData.attachment.name,
    },
    headers
  );

  const uploadDocURL = await uploadWithSignedUrl(
    formData.attachment,
    JSON.parse(result[0]).signed_url,
    formData.attachment.type
  );

  const supportFormData = { ...formData };
  supportFormData.attach_link = uploadDocURL;
  delete supportFormData.attachment;

  await submitSupportForm(supportFormData);
};

/**
 * Submits the support form data to the API endpoint.
 *
 * @param {Object} formData - The support form data containing user input fields.
 * @returns {Promise<any>} - The API response.
 *
 * This function makes a POST request to submit the support form data.
 * It sends the data as JSON with appropriate headers and returns the response from the API.
 */
export const submitSupportForm = async (formData) => {
  const url =
    "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/v1/no_support/support-form";
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  return await postRequest(url, JSON.stringify(formData), headers);
};

/**
 * Fetches data for a selected application based on the given application name.
 *
 * @param {string} selectedApplicationName - The ID of the selected application.
 * @returns {Promise<any>} - A promise that resolves to the fetched application data.
 */
export const fetchApplicationDataByName = async (applicationName) => {
  const url = `https://lexai-company-analysis-318242574572.us-central1.run.app/analysis/company`;
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    company_name: applicationName,
    timeout: 300,
  };
  return await postRequest(url, payload, headers);
};

/**
 * Fetches data for a selected application based on the given application name.
 *
 * @param {string} selectedApplicationName - The ID of the selected application.
 * @returns {Promise<any>} - A promise that resolves to the fetched application data.
 */
export const fetchApplicationImg = async (website) => {
  const url = `https://cipilot-web-scrapper-318242574572.us-central1.run.app/api/v1/scrape/`;
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    website,
    include_webpages: false,
    recursion_depth: 1,
  };
  return await postRequest(url, payload, headers);
};

/**
 * Fetches data for a selected application based on the given application ID.
 *
 * @param {string} selectedApplicationId - The ID of the selected application.
 * @returns {Promise<any>} - A promise that resolves to the fetched application data.
 */
export const fetchApplicationDataByIdAndName = async (
  applicationName,
  applicationId = ""
) => {
  const url = `https://lexai-company-analysis-318242574572.us-central1.run.app/analysis/company_init`;
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const payload = {
    company_name: applicationName,
    application_id: applicationId,
    check_db_by_name: true,
    timeout: 300,
  };
  return await postRequest(url, payload, headers);
};

/**
 * Fetches application data based on the provided filter parameters.
 *
 * @param {Record<string, any>} filters - An object containing query parameters for filtering applications.
 * @param {string} accessToken - The access token for authentication.
 * @param {string} refreshToken - The refresh token for authentication.
 * @returns {Promise<any>} A promise that resolves to the fetched application data.
 */
export const fetchDataForMultipleSelect = async (
  filters,
  dateFilters,
  accessToken,
  refreshToken,
  page = 1,
  limit = 15
) => {
  const skip = (page - 1) * limit;
  const dateParam = generateDateQueryParams(dateFilters);
  const headers = {
    "Content-Type": "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken,
  };

  const baseUrl =
    "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/application_filter";

  // Construct query parameters dynamically
  const queryParams = new URLSearchParams();

  for (const key in filters) {
    if (Array.isArray(filters[key])) {
      // Append multiple values for the same key
      filters[key].forEach((value) => queryParams.append(key, value));
    } else {
      queryParams.append(key, filters[key]);
    }
  }

  let encodedQueryString =
    queryParams && queryParams.toString().replace(/\+/g, "%20");
  // Append dateParam if it exists and queryParams is not empty
  if (dateParam) {
    encodedQueryString = encodedQueryString
      ? `${encodedQueryString}&${dateParam}`
      : dateParam;
  }
  const url = `${baseUrl}?${encodedQueryString}&skip=${skip}&limit=${limit}`;
  return await getRequest(url, headers);
};

/**
 * Fetches total application count
 *
 * @param {string} accessToken - The access token for authentication.
 * @param {string} refreshToken - The refresh token for authentication.
 * @returns {Promise<any>} A promise that resolves to the total application count.
 */
export const fetchTotalApplications = async (accessToken, refreshToken) => {
  const headers = {
    Accept: "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken,
  };

  const url =
    "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/total_filtered_applications";

  return await getRequest(url, headers);
};

/**
 * Fetches uniques values for each column
 *
 * @param {string} accessToken - The access token for authentication.
 * @param {string} refreshToken - The refresh token for authentication.
 * @returns {Promise<any>} A promise that resolves to uniques values for each column.
 */
export const fetchAppUniqueValues = async (accessToken, refreshToken) => {
  const headers = {
    Accept: "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken,
  };

  const url =
    "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/application_unique_values";

  return await getRequest(url, headers);
};

/**
 * Fetches total application count based on filters
 *
 * @param {string} accessToken - The access token for authentication.
 * @param {string} refreshToken - The refresh token for authentication.
 * @returns {Promise<any>} A promise that resolves to the total application count.
 */
export const fetchFilterTotalApplications = async (
  filters,
  dateFilters,
  accessToken,
  refreshToken
) => {
  const headers = {
    Accept: "application/json",
    "X-Access-Token": accessToken,
    "X-Refresh-Token": refreshToken,
  };

  const dateParam = generateDateQueryParams(dateFilters);
  const baseUrl =
    "https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/total_filtered_applications";

  // Construct query parameters dynamically
  const queryParams = new URLSearchParams();

  for (const key in filters) {
    if (Array.isArray(filters[key])) {
      // Append multiple values for the same key
      filters[key].forEach((value) => queryParams.append(key, value));
    } else {
      queryParams.append(key, filters[key]);
    }
  }

  let encodedQueryString =
    queryParams && queryParams.toString().replace(/\+/g, "%20");
  // Append dateParam if it exists and queryParams is not empty
  if (dateParam) {
    encodedQueryString = encodedQueryString
      ? `${encodedQueryString}&${dateParam}`
      : dateParam;
  }
  const url = `${baseUrl}?${encodedQueryString}`;

  return await getRequest(url, headers);
};
